import { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Clock, CheckCircle, AlertTriangle, Mail, Phone, Calendar, DollarSign } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PageHeader } from '@/components/PageHeader';
import { useCollectionWorkflows } from '@/hooks/useCollectionWorkflows';
import { useCollectionActivities } from '@/hooks/useCollectionActivities';
import { usePaymentPlans } from '@/hooks/usePaymentPlans';

export default function CollectionDashboard() {
  const { workflows } = useCollectionWorkflows();
  const { activities } = useCollectionActivities();
  const { plans } = usePaymentPlans();

  // Mock real-time data - replace with actual real-time hooks
  const [realtimeStats, setRealtimeStats] = useState({
    totalRequests: 1247,
    pendingResponses: 89,
    successRate: 78.3,
    avgResponseTime: '2.4 days',
    todayRequests: 23,
    weekTrend: 12.5
  });

  const metrics = [
    {
      title: "Total Collection Requests",
      value: realtimeStats.totalRequests.toLocaleString(),
      description: `+${realtimeStats.todayRequests} today`,
      icon: Mail,
      trend: realtimeStats.weekTrend > 0 ? 'up' : 'down',
      trendValue: `${Math.abs(realtimeStats.weekTrend)}%`
    },
    {
      title: "Pending Responses",
      value: realtimeStats.pendingResponses,
      description: "Awaiting supplier action",
      icon: Clock,
      trend: 'neutral'
    },
    {
      title: "Success Rate",
      value: `${realtimeStats.successRate}%`,
      description: "Bills collected successfully",
      icon: CheckCircle,
      trend: 'up',
      trendValue: "3.2%"
    },
    {
      title: "Avg Response Time",
      value: realtimeStats.avgResponseTime,
      description: "Supplier response time",
      icon: TrendingUp,
      trend: 'down',
      trendValue: "0.3 days"
    }
  ];

  const recentActivities = activities.slice(0, 5).map(activity => ({
    id: activity.id,
    type: activity.activity_type,
    status: activity.status,
    supplier: `Supplier ${activity.bill_id.slice(0, 8)}`,
    timestamp: new Date(activity.created_at).toLocaleString(),
    amount: '$' + ((Math.random() * 1000 + 100) || 0).toFixed(2)
  }));

  const activeWorkflows = workflows.filter(w => w.is_active);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Collection Dashboard"
        description="Monitor and manage your bill collection activities in real-time"
      />

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric, index) => (
          <Card key={index} className="relative overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {metric.title}
              </CardTitle>
              <metric.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metric.value}</div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                {metric.trend === 'up' && <TrendingUp className="h-3 w-3 text-green-500" />}
                {metric.trend === 'down' && <TrendingDown className="h-3 w-3 text-red-500" />}
                {metric.trendValue && (
                  <span className={metric.trend === 'up' ? 'text-green-500' : 'text-red-500'}>
                    {metric.trendValue}
                  </span>
                )}
                <span>{metric.description}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid w-full lg:w-[400px] grid-cols-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="workflows">Workflows</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Activities */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Activities</CardTitle>
                <CardDescription>Latest collection requests and responses</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {recentActivities.map((activity) => (
                    <div key={activity.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-muted">
                          {activity.type === 'email' && <Mail className="h-4 w-4" />}
                          {activity.type === 'call' && <Phone className="h-4 w-4" />}
                          {activity.type === 'letter' && <Calendar className="h-4 w-4" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{activity.supplier}</p>
                          <p className="text-xs text-muted-foreground">{activity.timestamp}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant={
                            activity.status === 'delivered' ? 'default' :
                            activity.status === 'pending' ? 'secondary' :
                            activity.status === 'failed' ? 'destructive' : 'outline'
                          }
                        >
                          {activity.status}
                        </Badge>
                        <span className="text-sm font-medium">{activity.amount}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Supplier Response Rates */}
            <Card>
              <CardHeader>
                <CardTitle>Supplier Performance</CardTitle>
                <CardDescription>Response rates by supplier type</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { name: 'Energy Suppliers', rate: 85, total: 45 },
                  { name: 'Water Utilities', rate: 92, total: 23 },
                  { name: 'Telecoms', rate: 67, total: 31 },
                  { name: 'Gas Suppliers', rate: 78, total: 28 }
                ].map((supplier) => (
                  <div key={supplier.name} className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>{supplier.name}</span>
                      <span className="text-muted-foreground">{supplier.rate}% ({supplier.total} requests)</span>
                    </div>
                    <Progress value={supplier.rate} className="h-2" />
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Active Payment Plans */}
          <Card>
            <CardHeader>
              <CardTitle>Active Payment Plans</CardTitle>
              <CardDescription>Currently managed payment arrangements</CardDescription>
            </CardHeader>
            <CardContent>
              {plans.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No active payment plans
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Plan Type</TableHead>
                      <TableHead>Total Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Next Payment</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {plans.slice(0, 5).map((plan) => (
                      <TableRow key={plan.id}>
                        <TableCell className="font-medium">
                          Customer {plan.customer_id.slice(0, 8)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{plan.plan_type}</Badge>
                        </TableCell>
                        <TableCell>${plan.total_amount}</TableCell>
                        <TableCell>
                          <Badge variant={plan.status === 'active' ? 'default' : 'secondary'}>
                            {plan.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(plan.start_date).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="workflows" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Active Workflows</CardTitle>
              <CardDescription>Monitor workflow performance and status</CardDescription>
            </CardHeader>
            <CardContent>
              {activeWorkflows.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No active workflows
                </div>
              ) : (
                <div className="space-y-4">
                  {activeWorkflows.map((workflow) => (
                    <div key={workflow.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <h4 className="font-medium">{workflow.name}</h4>
                        <p className="text-sm text-muted-foreground">{workflow.description}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <Badge variant="default">Active</Badge>
                        <Badge variant="outline">
                          {Array.isArray(workflow.steps) ? workflow.steps.length : 0} steps
                        </Badge>
                        <Badge variant="secondary">{workflow.workflow_type}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Success Rate Trends</CardTitle>
                <CardDescription>Collection success over time</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8 text-muted-foreground">
                  Chart visualization would go here
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Response Time Analysis</CardTitle>
                <CardDescription>Average supplier response times</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8 text-muted-foreground">
                  Chart visualization would go here
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}